import React, { useCallback, useEffect, useRef } from 'react';
import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './tablev3.css';
import '../Home/classess.css';
import { IHistorySession } from './IDashboard';
import { getSessionsWithShares, mergeSessions } from '../../api/SessionsService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DateTime } from 'luxon';
import { ArrowLeft, ArrowRight } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
  setHistoryDate,
  setIsDashboardLoading,
  setTableRows,
  setTableScrollPosition,
  updateDashboardRefresh,
} from '../../redux/features/app/app';
import Table from './Table/Table';
import { SortDirection } from '../../redux/store/IStore';
import { transformFilters } from '../../shared/filterTransformer';
import { IModal } from '../Editor/IEditor';

interface V3TableProps {
  modal: IModal;
  setModal: React.Dispatch<React.SetStateAction<IModal>>;
}

const V3Table = ({ modal, setModal }: V3TableProps) => {
  //const [data, setData] = useState<IHistorySession[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRows = useAppSelector((state) => state.app.tableRows);
  const tableScrollPosition = useAppSelector((state) => state.app.tableScrollPosition);
  const startDate = useAppSelector((state) => state.app.historyDate);
  const refreshDashboard = useAppSelector((state) => state.app.refreshDashboard);
  const isDashboardReady = useAppSelector((state) => state.app.isDashboardReady);
  const appliedFilters = useAppSelector((state) => state.app.dashboardFilters);
  const dispatch = useAppDispatch();
  const [isMergeModalVisible, setIsMergeModalVisible] = useState<boolean>(false);
  const [stateMergeSessionIds, setMergeSessionIds] = useState<number[]>([]);
  const [stateMergeSessionNames, setMergeSessionNames] = useState<string[]>([]);

  const closeModal = () => {
    setIsMergeModalVisible(false);
    setMergeSessionIds([]);
    setMergeSessionNames([]);
  };

  const onDateChange = (date: Date | null) => {
    if (!date) return;
    dispatch(setHistoryDate(date));
    dispatch(setTableScrollPosition(0));
  };

  const { enqueueSnackbar } = useSnackbar();

  const updateData = (newData: IHistorySession[]) => dispatch(setTableRows(newData));

  const mergeAfterConfirm = async () => {
    setIsMergeModalVisible(false);
    enqueueSnackbar('Združevanje poteka. Seja bo kmalu na voljo.', { variant: 'info' });
    const mapped = tableRows.map((row) => {
      row.merging = row.id === stateMergeSessionIds[0] && row.isHeader;
      return row;
    });
    updateData(mapped);

    try {
      await mergeSessions(stateMergeSessionIds, stateMergeSessionNames[0]);
    } catch (error) {
      console.log(error);
      const mappedError = error as any;
      if (mappedError.response.status === 423) {
        enqueueSnackbar('Seja se združuje ali ureja.', { variant: 'info' });
      }
      enqueueSnackbar('Pri združevanju je prišlo do napake. Kontaktirajte tehnično podporo', {
        variant: 'error',
      });
    }

    const mappedAfter = tableRows.map((row) => {
      row.merging = row.id === stateMergeSessionIds[0] && row.isHeader;
      return row;
    });
    updateData(mappedAfter);

    setMergeSessionIds([]);
    setMergeSessionNames([]);
  };

  const onChangeRaw = (
    event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined
  ) => {
    console.log(event);
  };

  const addDay = () => {
    const newDate = DateTime.fromISO(startDate.toISOString()).plus({ day: 1 }).toISO();
    onDateChange(new Date(newDate as string));
    dispatch(updateDashboardRefresh(true));
  };

  const subtractDay = () => {
    const newDate = DateTime.fromISO(startDate.toISOString()).minus({ day: 1 }).toISO();
    onDateChange(new Date(newDate as string));
    dispatch(updateDashboardRefresh(true));
  };

  const getInitialData = useCallback(async () => {
    if (!isDashboardReady && !refreshDashboard) return;

    dispatch(setIsDashboardLoading(true));

    const filters: URLSearchParams = transformFilters(appliedFilters);
    filters.delete('created-at');

    if (!startDate) throw new Error(`Invalid date filter`);
    const month = String(startDate.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(startDate.getDate()).padStart(2, '0');
    const year = startDate.getFullYear();

    const nameFilter = `%${year}-${month}-${day}%`;
    //filters.append("created-at", `${startDate};${endDate}`)
    filters.append('name', nameFilter);
    const { sessions, last } = await getSessionsWithShares(undefined, 2000, filters, {
      columnName: 'createdAt',
      type: SortDirection.desc,
    });

    const sessionNameSet = new Set(sessions.map((session) => session.content.name.split('/')[0]));

    if (sessionNameSet.size < 1) {
      updateData([]);
      //setHasMore(false);
      dispatch(setIsDashboardLoading(false));
      return;
    }

    const prefixFilters: URLSearchParams = new URLSearchParams();
    sessionNameSet.forEach((name) => prefixFilters.append('name', `${name}%`));

    const { sessions: sessionsPrefixed, last: lastPrefixed } = await getSessionsWithShares(
      undefined,
      2000,
      prefixFilters,
      { columnName: 'name', type: SortDirection.asc }
    );

    const mapped: IHistorySession[] = sessionsPrefixed
      .filter((session) => !(session.content.isDiscarded && session.content.sources.includes('MERGED')))
      .map((session) => {
        return {
          ...session.content,
          shares: session.shares,
          cursor: session.cursor,
          expanded:
            tableRows.filter(
              (currentSession) =>
                currentSession.id === session.content.id &&
                currentSession.expanded &&
                !currentSession.isHeader
            ).length === 1,
          tagged: false,
          openedVersion: false,
          openedLabels: false,
          summary: session.summary,
          recordings: session.recordings,
          isHeader: false,
          merging: false,
        };
      })
      .sort((a, b) => {
        // First, sort by name
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        // If names are the same, sort by isDiscarded
        return a.isDiscarded === b.isDiscarded ? 0 : a.isDiscarded ? 1 : -1;
      });

    const mappedResults: IHistorySession[] = [];

    let previousPrefix: string = '';
    mapped.forEach((session) => {
      const prefix = session.name.split('/')[0];
      if (prefix !== previousPrefix) {
        mappedResults.push({
          ...session,
          expanded: false,
          isHeader: true,
          name: prefix,
        });
        previousPrefix = prefix;
      }

      mappedResults.push(session);
    });

    dispatch(setIsDashboardLoading(false));
    dispatch(updateDashboardRefresh(false));
    updateData(mappedResults);
  }, [tableRows, refreshDashboard, startDate, isDashboardReady]);

  useEffect(() => {
    const interval = setInterval(async () => getInitialData(), 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [tableRows, startDate]);

  useEffect(() => {
    getInitialData();
  }, [startDate, refreshDashboard]);

  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (tableRows.length > 0 && scrollContainerRef.current && tableScrollPosition > 0) {
      scrollContainerRef.current.scrollTop = tableScrollPosition;
    }
  }, [tableRows]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    dispatch(setTableScrollPosition((e.target as HTMLDivElement).scrollTop));
  };

  return (
    <div style={{ backgroundColor: '#f7f7f7' }}>
      <div style={{ zIndex: 1000 }} id="date-picker-container">
        <div style={{ cursor: 'pointer' }} onClick={subtractDay}>
          <ArrowLeft />
        </div>
        <DatePicker
          selected={startDate}
          className="date_input"
          value={`${startDate.getDate()}.${startDate.getMonth() + 1}.${startDate.getFullYear()}`}
          onChangeRaw={onChangeRaw}
          onChange={onDateChange}
        />
        <div style={{ cursor: 'pointer' }} onClick={addDay}>
          <ArrowRight />
        </div>
      </div>
      <div style={{ width: '80vw', margin: '0 auto', overflowX: 'scroll' }}>
        <div
          id="table_scroll_container"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{ height: '60vh', overflow: 'scroll' }}
        >
          <DndProvider backend={HTML5Backend}>
            {/*<NewTable updateData={() => {}} data={tableRows} />*/}
            <Table modal={modal} setModal={setModal} />
          </DndProvider>
        </div>
      </div>
    </div>
  );
};

export default V3Table;
